import moment from 'moment'

// Shared formatting + domain helpers for the CRM admin pages.

export const formatDate = (value) =>
  value ? moment(value).format('D MMM YYYY') : ''

export const formatDateTime = (value) =>
  value ? moment(value).format('D MMM YYYY, HH:mm') : ''

export const fromNow = (value) => (value ? moment(value).fromNow() : '')

export const toDateInput = (value) =>
  value ? moment(value).format('YYYY-MM-DD') : ''

export const formatCurrency = (amount, currency = 'GBP') => {
  if (amount === undefined || amount === null || amount === '') return ''
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency || 'GBP',
    maximumFractionDigits: 0,
  }).format(Number(amount))
}

// 1500 → '1,500'; 231.5 → '231.5'. Empty for anything that is not a number.
export const formatNumber = (value, options) => {
  if (value === undefined || value === null || value === '') return ''
  const number = Number(value)
  return Number.isFinite(number) ? number.toLocaleString('en-GB', options) : ''
}

// A usage month as the server labels it ('2026-08') → 'Aug 2026'. The label is
// only ever formatted, never turned into dates: the server owns month
// boundaries (Europe/London).
export const formatPeriod = (period, format = 'MMM YYYY') => {
  const month = moment(period, 'YYYY-MM', true)
  return month.isValid() ? month.format(format) : period || ''
}

export const TIER_NAMES = { 1: 'Core', 2: 'Premium', 3: 'Enterprise', 4: 'Custom' }

export const tierLabel = (tier, fallback = 'No plan') =>
  TIER_NAMES[Number(tier)] ?? fallback

export const rootDomain = () => process.env.ROOT_DOMAIN || 'ambunet.co.uk'

export const tenantUrl = (subdomain) =>
  subdomain ? `https://${subdomain}.${rootDomain()}` : null

// ---------------------------------------------------------------------------
// Regions
// ---------------------------------------------------------------------------

// Each region runs its own stack with its own CRM at crmadmin.<domain>.
// Mirrors COUNTRY_OPTIONS in mobile/src/utils/region.js; flip `live` when a
// region's host exists.
export const CRM_REGIONS = [
  { key: 'UK', label: 'United Kingdom', flag: '🇬🇧', domain: 'ambunet.co.uk', live: true },
  { key: 'IE', label: 'Ireland', flag: '🇮🇪', domain: 'ambunet.ie', live: true },
  { key: 'WALES', label: 'Wales', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', domain: 'ambunet.wales', live: false },
  { key: 'DE', label: 'Germany', flag: '🇩🇪', domain: 'ambunet.de', live: false },
  { key: 'EU', label: 'Europe', flag: '🇪🇺', domain: 'ambu.net', live: false },
]

export const currentRegion = () => {
  const { hostname } = window.location
  return (
    CRM_REGIONS.find((region) => hostname === region.domain || hostname.endsWith(`.${region.domain}`)) ??
    // Local dev: the hostname is localhost, so fall back to the built-in root domain.
    CRM_REGIONS.find((region) => region.domain === rootDomain()) ??
    CRM_REGIONS[0]
  )
}

// The same CRM page on another region's site.
export const crmUrlForRegion = (region) =>
  `https://crmadmin.${region.domain}${window.location.pathname}${window.location.search}`

// ---------------------------------------------------------------------------
// Sales pipeline
// ---------------------------------------------------------------------------

export const STAGES = [
  { key: 'New', label: 'New', color: '#268EBB', hint: 'Not yet spoken to' },
  {
    key: 'Contacted',
    label: 'Contacted',
    color: '#F28B1F',
    hint: 'Conversation started',
  },
  {
    key: 'Engaged',
    label: 'Engaged',
    color: '#85276A',
    hint: 'Actively in conversation',
  },
  {
    key: 'Converted',
    label: 'Customer',
    color: '#00964C',
    hint: 'Paying subscriber',
  },
  { key: 'Lost', label: 'Lost', color: '#64748B', hint: 'Not proceeding' },
]

// Trials no longer exist. Leads still carrying this stage are shown, but it is
// never offered when moving a lead.
export const LEGACY_STAGE = {
  key: 'Trial Started',
  label: 'Trialled',
  color: '#557A95',
  hint: 'Legacy trial stage',
}

// 'Demoed' was replaced by 'Engaged' on 2026-09-10 (a demo is now the
// per-lead demoAt flag). The API normalises it on read; this is belt and
// braces for anything cached client-side.
const stageAlias = (key) => (key === 'Demoed' ? 'Engaged' : key)

export const stageMeta = (rawKey) => {
  const key = stageAlias(rawKey)
  return (
    STAGES.find((stage) => stage.key === key) ??
    (key === LEGACY_STAGE.key ? LEGACY_STAGE : { key, label: key || 'New', color: '#64748B' })
  )
}

export const OPEN_STAGE_KEYS = ['New', 'Contacted', 'Engaged', 'Trial Started', 'Demoed']

export const isOpenStage = (stage) => OPEN_STAGE_KEYS.includes(stage ?? 'New')

export const SOURCE_OPTIONS = [
  'Website',
  'Early Access Form',
  'Word of Mouth',
  'Another Client',
  'Referral',
  'LinkedIn',
  'Facebook',
  'Instagram',
  'Event / Exhibition',
  'ESS 2025',
  'Trial signup',
  'Tenant signup',
  'Cold outreach',
  'Other',
]

export const INTEREST_LABELS = {
  1: 'Cold',
  2: 'Low',
  3: 'Warm',
  4: 'Keen',
  5: 'Hot',
}

export const interestColor = (level) => {
  const value = Number(level)
  if (value >= 4) return '#00964C'
  if (value === 3) return '#F28B1F'
  if (value >= 1) return '#94A3B8'
  return '#CBD5E1'
}

export const followUpState = (value) => {
  if (!value) return null
  const date = moment(value)
  const today = moment().endOf('day')
  if (date.isBefore(moment().startOf('day')))
    return { key: 'overdue', label: `Overdue ${date.fromNow()}`, color: '#E32525' }
  if (date.isSameOrBefore(today))
    return { key: 'today', label: 'Due today', color: '#F28B1F' }
  return { key: 'upcoming', label: date.format('D MMM'), color: '#64748B' }
}

// ---------------------------------------------------------------------------
// Tenants
// ---------------------------------------------------------------------------

export const subscriptionChip = (tenant) => {
  if (!tenant) return { label: 'Unknown', color: 'default', variant: 'outlined' }
  if (tenant.isDemo) return { label: 'Demo tenant', color: 'anPurple' }
  if (tenant.isActive === false)
    return { label: 'Disabled', color: 'default', variant: 'outlined' }
  if (tenant.activeSubscription === false || tenant.inInitialTrialPeriod === true) {
    if (tenant.trialEnd) {
      const ended = moment(tenant.trialEnd).isBefore(moment())
      return {
        label: ended ? 'Trial ended' : 'Trial (legacy)',
        color: ended ? 'default' : 'anPurple',
        variant: ended ? 'outlined' : 'filled',
      }
    }
    return { label: 'Not subscribed', color: 'default', variant: 'outlined' }
  }
  // Invoiced outside Stripe: live by definition, whatever Stripe last said.
  if (tenant.manualBilling === true)
    return { label: 'Manual billing', color: 'anGreen' }
  if (tenant.subscriptionCancellationDate)
    return { label: 'Cancelling', color: 'anOrange' }
  switch (tenant.stripeSubscriptionStatus) {
    case 'active':
      return { label: 'Active', color: 'anGreen' }
    case 'trialing':
      return { label: 'Stripe trial', color: 'anPurple' }
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
    case 'incomplete_expired':
      return { label: 'Payment issue', color: 'anRed' }
    case 'paused':
      return { label: 'Paused', color: 'anOrange' }
    case 'canceled':
      return { label: 'Cancelled', color: 'anOrange' }
    default:
      return { label: 'Active (no Stripe)', color: 'anGreen', variant: 'outlined' }
  }
}

export const lastActivityColour = (value) => {
  if (!value) return 'text.disabled'
  const daysAgo = moment().diff(moment(value), 'days')
  if (daysAgo <= 2) return 'anGreen.main'
  if (daysAgo <= 7) return 'anOrange.main'
  return 'anRed.main'
}

export const monthlyFee = (contract = {}) => {
  if (
    String(contract.billingCadence || '').startsWith('annual') &&
    Number(contract.annualFee) > 0
  ) {
    return Math.round(Number(contract.annualFee) / 12)
  }
  if (Number(contract.monthlyFee) > 0) return Number(contract.monthlyFee)
  if (Number(contract.annualFee) > 0)
    return Math.round(Number(contract.annualFee) / 12)
  return null
}

export const initials = (name = '') =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?'
